import React, { useMemo, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import moment from "moment";
import { makeStyles, useTheme } from "@mui/styles";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
// import { DataGrid } from '@mui/x-data-grid';
import { TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Alert } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import red from "@mui/material/colors/red";
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Chip,
  List,
  ListItem,
  Grid,
  useMediaQuery,
} from "@mui/material";

//import { getInitials } from 'helpers';
import { useHistory } from "react-router-dom";
import {
  setTransactionDetail,
  setTransactionPage,
  setTransactionIds,
} from "store/actions/transaction";
import { useDispatch, useSelector } from "react-redux";

import { setOrderDetail } from "store/actions/restaurant";
import { transformOrderId } from "util/orderUtils";
import Papa from 'papaparse';
import { EMPTY_FEE_SUMMARY, feeSummariesByTransaction, formatFee } from "util/processingFeeUtils";
import { saveAs } from 'file-saver';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0,
  },
  inner: {},
  table: {
    minWidth: 1050,
  },
  nameContainer: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  actions: {
    justifyContent: "flex-end",
  },
  textLink: {
    textDecoration: "underline",
  },
  downloadRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    paddingBottom: "1rem",
    [theme.breakpoints.down("sm")]: {
      "& > button": {
        width: "100%",
      },
    },
  },
  // NOTE: the search field's width lives in `sx` (see renderSearchField) — a
  // makeStyles class loses to the MUI component's own emotion class.
  // --- Compact (phone / folded foldable / small tablet) card list ---
  cardListHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 1.5, 0, 1.5),
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
  listCard: {
    padding: theme.spacing(1.5),
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  cardTitle: {
    minWidth: 0,
    flex: 1,
  },
  cardTotal: {
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  cardChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  cardOrders: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  cardFields: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  cardValue: {
    fontSize: "0.85rem",
    overflowWrap: "anywhere",
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
  pagination: {
    width: "100%",
    "& .MuiTablePagination-toolbar": {
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTablePagination-toolbar": {
        paddingLeft: theme.spacing(1),
      },
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        fontSize: "0.75rem",
        margin: 0,
      },
      "& .MuiTablePagination-actions": {
        marginLeft: theme.spacing(1),
      },
    },
  },
}));

const TransactionsTable = (props) => {
  const { className, transactions, processingFees, ...rest } = props;

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();

  // Below `md` a 9 column table cannot be read without horizontal scrolling,
  // so phones, folded foldables and portrait tablets get a stacked card list.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [keyword, setKeyword] = useState("");


  const updateTransactionDetail = (transaction) =>
    dispatch(setTransactionDetail(transaction));
  const updateTransactionIds = (ids) => dispatch(setTransactionIds(ids));
  const showOrderDetail = (order) => dispatch(setOrderDetail(order));
  const setPage = (page) => dispatch(setTransactionPage(page));
  const page = useSelector((state) => state.transaction.page);
  const user = useSelector((state) => state.account.user);
  const currencySymbol = (user && user.currencySymbol) ? user.currencySymbol : "$";
  
  // Processing fee (net of commission handed back on refunds) and refund cost per transaction uuid.
  // Informational only: never added into any total.
  const feeSummaries = useMemo(
    () => feeSummariesByTransaction(processingFees, transactions),
    [processingFees, transactions]
  );
  const feesOf = (transaction) => feeSummaries.get(transaction.uuid) || EMPTY_FEE_SUMMARY;
  const feeCsvValue = (amount) => (amount ? amount.toFixed(2) : "");

  // Check if user has permission to download reports
  const canDownloadReports = user && ['admin', 'restaurantAdmin', 'groupAdmin', 'menuAdmin'].includes(user.role);

  const handleSelectOne = (event, transactionId) => {
    const selectedIndex = selectedTransactions.indexOf(transactionId);
    let newSelectedTransactions = [];

    if (selectedIndex === -1) {
      newSelectedTransactions = newSelectedTransactions.concat(
        selectedTransactions,
        transactionId
      );
    } else if (selectedIndex === 0) {
      newSelectedTransactions = newSelectedTransactions.concat(
        selectedTransactions.slice(1)
      );
    } else if (selectedIndex === selectedTransactions.length - 1) {
      newSelectedTransactions = newSelectedTransactions.concat(
        selectedTransactions.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedTransactions = newSelectedTransactions.concat(
        selectedTransactions.slice(0, selectedIndex),
        selectedTransactions.slice(selectedIndex + 1)
      );
    }

    setSelectedTransactions(newSelectedTransactions);
    updateTransactionIds(newSelectedTransactions);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
  };

  const getTimeString = (date) => {
    if (typeof date === "object" && "_seconds" in date) {
      return moment(new Date(date._seconds * 1000)).format(
        "DD/MM/YYYY HH:mm:ss"
      );
    }
    return moment(date).format("DD/MM/YYYY HH:mm:ss");
  };

  const getTimeString2 = (date) => {
    if (!date) {
      return "";
    }
    // console.log('date', date);
    if (`${date}`.includes("/")) {
      return date;
    }

    if (typeof date === "object" && "_seconds" in date) {
      return moment(new Date(date._seconds * 1000)).format(
        "DD/MM/YYYY HH:mm:ss"
      );
    }
    return moment(date).format("DD/MM/YYYY HH:mm:ss");
  };

  const openTransactionDetail = (e, transaction) => {
    updateTransactionDetail(transaction);
    history.push("/transaction");
  };

  const transferLocalDishes = (dishes) => {
    let newDishes = [];
    dishes.forEach((dish) => {
      newDishes.push({
        dish: dish,
        quantity: dish.quantity,
        subTotal: dish.subTotal,
        specialString: dish.specialString,
      });
    });
    return newDishes;
  };

  const transferLocalOrder = (order) => {
    if (!order) {
      return;
    }

    const restaurant = { tableId: order.tableNumber };
    const newOrder = {
      id: order.orderNumber,
      createdAt: order.createdAt,
      status: order.status,
      restaurant: restaurant,
      uuid: order.uuid,
      order: {
        items: transferLocalDishes(order.dishes),
      },
      user: {
        email: order.customerEmail,
        phoneNumber: order.customerPhone,
        name: order.customerName,
        postcode: order.customerPostcode,
      },
    };
    return newOrder;
  };

  const openOrderDetail = (e, order) => {
    showOrderDetail(transferLocalOrder(order));
    history.push(`/orderDetail/${order.orderNumber}`);
  };

  const getPaymentTypes = (transaction) => {
    if (!transaction) {
      return "";
    }
 

    //console.log('transaction.operationHistory', transaction.operationHistory, transaction.paymentTypes);
    if (transaction.paymentTypes && transaction.paymentTypes.length > 0) {
      // remove duplicates paymentType
      const paymentTypes = [
        ...new Set(
          transaction.paymentTypes.map((paymentType) => paymentType.type)
        ),
      ];

      return paymentTypes.join(",");
    } else {
      if (
        transaction.operationHistory &&
        transaction.operationHistory.length > 0
      ) {
        let filterOperations = transaction.operationHistory.filter(
          (operation) => operation.operationType === "Payment"
        );
        return filterOperations
          .map((operation) => operation.operationName)
          .join(",");
      }
    }
    if (transaction.deliveryPartnerType && transaction.deliveryPartnerType !== '' && !transaction.deliveryPartnerPostpaid) {
      return transaction.deliveryPartnerType;
    }

    return "";

    
  };

  const filterTransactions = transactions.filter((transaction) => {
    if (keyword === "") {
      return true;
    }
    const lowcaseKey = keyword.toLocaleLowerCase();
    if (transaction.uuid.toLowerCase().includes(lowcaseKey)) {
      return true;
    }
    if (transaction.operationString.toLowerCase().includes(lowcaseKey)) {
      return true;
    }

    if (transaction.tableNo.toLowerCase().includes(lowcaseKey)) {
      return true;
    }

    if (getPaymentTypes(transaction).toLowerCase().includes(lowcaseKey)) {
      return true;
    }

    return false;
  });

  const calculateTransactionTotal = () => {
    let total = 0;
    transactions.forEach((tran) => {
      if (selectedTransactions.includes(tran.uuid)) {
        total += Number(tran.totalSpending);
      }
    });
    return total.toFixed(2);
  };


  const downloadCSV = () => {
    const rows = filterTransactions.map((transaction) => {
      return {
        "Transaction ID": transaction.uuid && transaction.uuid.length > 6 ? transaction.uuid.substr(transaction.uuid.length - 6) : transaction.uuid,
        "Table No": transaction.tableNo,
        "Total": transaction.totalSpending.toFixed(2),
        "Payment Types": getPaymentTypes(transaction),
        "Payment Time": getTimeString2(transaction.payTime),
      };
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'transaction-report.csv');
  };

  // One row per payment. The transaction's processing fee and refund cost go on its first payment
  // row only, so summing either column gives the total once.
  const downloadPaymentTypesCSV = () => {
    const rows = filterTransactions.flatMap((transaction) => {
      const transactionId = transaction.uuid && transaction.uuid.length > 6 ? transaction.uuid.substr(transaction.uuid.length - 6) : transaction.uuid;
      const fees = feesOf(transaction);
      const paymentTypes = transaction.paymentTypes.map((paymentType, index) =>  {
      return {
        "Transaction ID": transactionId,
        "Payment Type": paymentType.type,
        "Payment Amount": paymentType.paymentAmount.toFixed(2),
        "Merchant Amount": paymentType.merchantAmount.toFixed(2),
        "Payment Time": getTimeString2(paymentType.time),
        "Processing Fee": index === 0 ? feeCsvValue(fees.processingFee) : "",
        "Refund Cost": index === 0 ? feeCsvValue(fees.refundFee) : "",
      };
    });
      
      return paymentTypes;
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'transaction-types-report.csv');
  };

  const downloadAdjustmentsCSV = () => {
    const rows = filterTransactions.flatMap((transaction) => {
      const transactionId = transaction.uuid && transaction.uuid.length > 6 ? transaction.uuid.substr(transaction.uuid.length - 6) : transaction.uuid;
      const paymentTypes = transaction.adjustments.map((adjustment) =>  {
      return {
        "Transaction ID": transactionId,
        "Adjustment Type": adjustment.adjustmentType,
        "Adjustment Amount": Number(adjustment.price).toFixed(2),
        "Adjustment Time": getTimeString2(adjustment.timeCreated),
      };
    });
      
      return paymentTypes;
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'adjustment-types-report.csv');
  };

  const downloadDishesCSV = () => {
    const rows = filterTransactions.flatMap((transaction) => {
      const transactionId = transaction.uuid && transaction.uuid.length > 6 ? transaction.uuid.substr(transaction.uuid.length - 6) : transaction.uuid;
      const dishes = transaction.orders.flatMap((order) => {
        const orderNumber = order.orderNumber;
        const orderDishes = order.dishes.map((dish) => {
          return {
            "Transaction ID": transactionId,
            "Order Number": orderNumber,
            "Dish Name": dish.name,
            "Dish Price": Number(dish.price).toFixed(2),
            "Dish Quantity": dish.quantity,
            "Dish Subtotal": Number(dish.subTotal).toFixed(2),
          };
        });
        return orderDishes;
      });
      return dishes;
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'dishes-report.csv');
  };


  // One slice, shared by both layouts, so page state (Redux) survives a resize.
  const pagedTransactions = filterTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const shortTransactionId = (transaction) =>
    transaction.uuid && transaction.uuid.length > 6
      ? transaction.uuid.substr(transaction.uuid.length - 6)
      : transaction.uuid;

  const isMarkedPaidByStaff = (transaction) =>
    (transaction.paymentTypes || []).some(
      (paymentType) =>
        paymentType.reference === "staff manual to mark payments as paid"
    );

  const CardField = ({ label, value }) => (
    <>
      <Typography component="dt" className={classes.cardLabel}>{label}</Typography>
      <Typography component="dd" className={classes.cardValue}>{value || "—"}</Typography>
    </>
  );

  CardField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
  };

  const renderSearchField = () => (
    <TextField
      sx={{ p: 1.5 }}
      placeholder="Search..."
      value={keyword}
      onChange={(e) => setKeyword(e.target.value)}
      variant="outlined"
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: keyword && (
          <InputAdornment position="end">
            <IconButton onClick={() => setKeyword("")} edge="end">
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const renderCards = () => (
    <>
      {renderSearchField()}
      <div className={classes.cardListHeader}>
        <Typography variant="body2" color="textSecondary">
          {filterTransactions.length} transaction
          {filterTransactions.length === 1 ? "" : "s"}
        </Typography>
      </div>
      <div className={classes.cardList}>
        {pagedTransactions.length === 0 ? (
          <Typography className={classes.emptyState} color="textSecondary">
            No transactions to show.
          </Typography>
        ) : (
          pagedTransactions.map((transaction) => (
            <Paper
              key={transaction.uuid}
              variant="outlined"
              className={classes.listCard}
              data-testid="transaction-card"
            >
              <div className={classes.cardHeader}>
                <Checkbox
                  checked={selectedTransactions.indexOf(transaction.uuid) !== -1}
                  color="primary"
                  onChange={(event) => handleSelectOne(event, transaction.uuid)}
                  size="small"
                  inputProps={{
                    "aria-label": `select transaction ${shortTransactionId(transaction)}`,
                  }}
                />
                <div className={classes.cardTitle}>
                  <Button
                    size="small"
                    onClick={(e) => openTransactionDetail(e, transaction)}
                  >
                    <span className={classes.textLink}>
                      {shortTransactionId(transaction)}
                    </span>
                  </Button>
                </div>
                <Typography className={classes.cardTotal}>
                  {currencySymbol}
                  {transaction.totalSpending.toFixed(2)}
                </Typography>
              </div>

              {transaction.isInvoicePrinted === true || isMarkedPaidByStaff(transaction) ? (
                <div className={classes.cardChips}>
                  {transaction.isInvoicePrinted === true ? (
                    <Chip
                      size="small"
                      label="Invoice Printed"
                      icon={<PrintIcon />}
                      style={{ backgroundColor: red[200] }}
                    />
                  ) : null}
                  {isMarkedPaidByStaff(transaction) ? (
                    <Chip
                      size="small"
                      label="Mark Paid by staff"
                      style={{ backgroundColor: red[100] }}
                    />
                  ) : null}
                </div>
              ) : null}

              <div className={classes.cardOrders}>
                {transaction.orders
                  .filter((order) => !order.permanentDeletion)
                  .map((order) => (
                    <Button
                      key={order.orderNumber}
                      size="small"
                      onClick={(e) => openOrderDetail(e, order)}
                    >
                      <span className={classes.textLink}>
                        {transformOrderId(order.orderNumber)}
                      </span>
                    </Button>
                  ))}
              </div>

              <dl className={classes.cardFields}>
                <CardField label="Table" value={transaction.tableNo} />
                <CardField label="Payment" value={getPaymentTypes(transaction)} />
                <CardField
                  label="Paid at"
                  value={getTimeString2(transaction.payTime)}
                />
                <CardField
                  label="Updated"
                  value={getTimeString(transaction.updatedAt)}
                />
                <CardField
                  label="Tax"
                  value={
                    transaction.taxAmount != null
                      ? `${currencySymbol}${Number(transaction.taxAmount).toFixed(2)}`
                      : "-"
                  }
                />
                <CardField
                  label="Processing Fee"
                  value={formatFee(feesOf(transaction).processingFee, currencySymbol)}
                />
                <CardField
                  label="Refund Cost"
                  value={formatFee(feesOf(transaction).refundFee, currencySymbol)}
                />
              </dl>
            </Paper>
          ))
        )}
      </div>
    </>
  );

  return (
    <Grid item lg={12} md={12} xl={12} xs={12}>
      {selectedTransactions.length > 0 ? (
        <Paper>
          <Alert severity="info">{`Selected transactions total amount: $${calculateTransactionTotal()}`}
          </Alert>
        </Paper>
      ) : null}
      {filterTransactions && filterTransactions.length > 0 && canDownloadReports ? (
        <div className={classes.downloadRow}>
            <Button variant="outlined" color="primary" onClick={downloadCSV} >
              Download Transactions CSV
            </Button>
            <Button variant="outlined" color="primary" onClick={downloadPaymentTypesCSV}>
              Download Payment Types CSV
            </Button>

            <Button variant="outlined" color="primary" onClick={downloadAdjustmentsCSV} >
              Download Adjustment Types CSV
            </Button>

            <Button variant="outlined" color="primary" onClick={downloadDishesCSV}>
              Download Dishes CSV
            </Button>
        </div>
      ) : null}

      <Card {...rest} className={clsx(classes.root, className)}>
        <CardContent className={classes.content}>
          {isCompact ? renderCards() : (
          <div className={classes.inner} >
            {renderSearchField()}

            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {/* <Checkbox
                      checked={selectedTransactions.length === transactions.length}
                      color="primary"
                      indeterminate={
                        selectedTransactions.length > 0 &&
                        selectedTransactions.length < transactions.length
                      }
                      onChange={handleSelectAll}
                    /> */}
                    </TableCell>
                    <TableCell>Orders</TableCell>
                    <TableCell>Updated Time</TableCell>
                    <TableCell>Payment Time</TableCell>
                    <TableCell>Table</TableCell>
                    <TableCell>Transaction Id</TableCell>
                    <TableCell>Payment Type</TableCell>
                    {/* <TableCell>Operations</TableCell> */}
                    <TableCell>TAX</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Processing Fee</TableCell>
                    <TableCell>Refund Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTransactions
                      .map((transaction) => (
                        <TableRow
                          className={classes.tableRow}
                          hover
                          key={transaction.uuid}
                          selected={
                            selectedTransactions.indexOf(transaction.uuid) !==
                            -1
                          }
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                selectedTransactions.indexOf(
                                  transaction.uuid
                                ) !== -1
                              }
                              color="primary"
                              onChange={(event) =>
                                handleSelectOne(event, transaction.uuid)
                              }
                              value="true"
                            />
                          </TableCell>

                          <TableCell>
                            <List dense disablePadding>
                              {transaction.orders
                                .filter((order) => !order.permanentDeletion)
                                .map((order) => (
                                  <ListItem key={order.orderNumber}>
                                    <Button
                                      onClick={(e) => openOrderDetail(e, order)}
                                    >
                                      <div className={classes.textLink}>
                                        <Typography variant="body1">
                                          {transformOrderId(order.orderNumber)}
                                        </Typography>
                                      </div>
                                    </Button>
                                  </ListItem>
                                ))}
                            </List>
                          </TableCell>

                          <TableCell>
                            {getTimeString(transaction.updatedAt)}
                          </TableCell>
                          <TableCell>
                            {getTimeString2(transaction.payTime)}
                          </TableCell>

                          <TableCell width="5%">
                            {transaction.tableNo}
                          </TableCell>

                          <TableCell>
                            <Button
                              onClick={(e) =>
                                openTransactionDetail(e, transaction)
                              }
                            >
                              <div className={classes.textLink}>
                                <Typography variant="body1">
                                  {
                                    // get last 6 letters of uuid
                                    transaction.uuid && transaction.uuid.length > 6 ? transaction.uuid.substr(transaction.uuid.length - 6) : transaction.uuid

                                  }
                                </Typography>
                             

                              </div>
                            </Button>
                            <br />
                            {transaction && transaction.isInvoicePrinted === true ? (
                              <Chip
                                size="small"
                                label="Invoice Printed"
                                icon={<PrintIcon />}
                                style={{ backgroundColor: red[200] }}
                                className={classes.marginRightBottom}
                              />
                            ) : null}
                               {transaction.paymentTypes.map((paymentType) => {
                                 if (paymentType.reference === 'staff manual to mark payments as paid') {
                                   return (
                                     <Chip
                                       key={paymentType.reference}
                                       size="small"
                                       label="Mark Paid by staff"
                                       style={{ backgroundColor: red[100], marginRight: '4px', marginBottom: '4px' }}
                                     />
                                   );
                                 }
                                 return null;
                               })}
                          </TableCell>
                          <TableCell width="10%">
                            {getPaymentTypes(transaction)}
                          </TableCell>


                          <TableCell>
                            {transaction.taxAmount != null
                              ? `${currencySymbol}${Number(transaction.taxAmount).toFixed(2)}`
                              : "-"}
                          </TableCell>

                          <TableCell>
                          {currencySymbol}{transaction.totalSpending.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {formatFee(feesOf(transaction).processingFee, currencySymbol)}
                          </TableCell>
                          <TableCell>
                            {formatFee(feesOf(transaction).refundFee, currencySymbol)}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          )}
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            className={classes.pagination}
            component="div"
            count={filterTransactions.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            // 50 is the initial rowsPerPage; without it here the select renders blank.
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
          />
        </CardActions>
      </Card>
    </Grid>
  );
};

TransactionsTable.propTypes = {
  className: PropTypes.string,
  transactions: PropTypes.array.isRequired,
  // Fee records from GET /processingFees covering these transactions (and their later refunds).
  processingFees: PropTypes.array,
};

export default TransactionsTable;
